import { useCallback, useRef } from 'react';

/**
 * Alternative to React.useCallback that gives a stable identity function
 * without a need to maintain dependencies
 *
 * Example:
 * ```
 * const [counter, setCounter] = useState(0);
 * const handleIncrement = useStableCallback(() => {
 *   // we always get up-to-date value of the counter here
 *   setCounter(counter + 1)
 * });
 * return <p>
 *   Counter: {counter}
 *   <IncrementButton
 *     // IncrementButton is rendered only once as
 *     // handleIncrement identity stays the same
 *     onIncrement={handleIncrement}
 *   />
 * </p>
 * ```
 */
export function useStableCallback<T extends (...args: any[]) => any>(
  callback: T,
): T {
  const currentCallbackRef = useRef<T>(callback);
  currentCallbackRef.current = callback;
  return useCallback(
    ((...args) => currentCallbackRef.current(...args)) as T,
    [],
  );
}
